import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Calendar, CalendarDays, ClipboardList, UtensilsCrossed, Plus, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BotanicalSprig from "@/components/planner/BotanicalSprig";
import ProfileMenu from "@/components/ProfileMenu";

/**
 * PlannerLayout — the self-contained Planner chrome.
 *
 * Deliberately does NOT render the main Willoah Navbar or AccountSetupBanner:
 * once a user enters the Planner, we want a peaceful, focused shell (matching
 * the mockups). Sign-out and profile still exist inside the Planner header at
 * the top right.
 *
 * Auth gate: if the user isn't signed in, we bounce to /signup with the
 * current Planner path stashed as `?returnTo=…` so the auth flow lands them
 * back where they were.
 *
 * Nav is unified across desktop + mobile:
 *   Home · Today · Week · Planner · Meals
 * Desktop: olive left sidebar with the Willoah PLANNER lockup at the top.
 * Mobile: bottom nav (5 slots) with a brass floating "+" that opens
 *         AddEventDialog — the same "+" the mockup shows.
 */
const NAV = [
  { to: "/planner/home", label: "Home", icon: Home, testid: "planner-nav-home" },
  { to: "/planner/today", label: "Today", icon: Calendar, testid: "planner-nav-today" },
  { to: "/planner/week", label: "Week", icon: CalendarDays, testid: "planner-nav-week" },
  { to: "/planner/calendar", label: "Planner", icon: ClipboardList, testid: "planner-nav-calendar" },
  { to: "/planner/meals", label: "Meals", icon: UtensilsCrossed, testid: "planner-nav-meals" },
];

export default function PlannerLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      const returnTo = encodeURIComponent(location.pathname + location.search);
      navigate(`/signup?returnTo=${returnTo}`, { replace: true });
    }
  }, [loading, user, navigate, location]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#F7F3ED] flex items-center justify-center text-[#3E4A37] text-sm">
        Loading Willoah Planner…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F3ED]" data-testid="planner-shell" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          data-testid="planner-sidebar"
          className="hidden md:flex flex-col w-64 min-h-screen bg-[#3E4A37] text-[#F7F3ED] p-6 sticky top-0 self-start relative overflow-hidden"
        >
          {/* Botanical corner accents on the sidebar — very low opacity so
              they read as texture, not decoration. Cream so they show
              faintly against the olive. */}
          <BotanicalSprig
            variant="branch"
            color="#F7F3ED"
            opacity={0.09}
            size={220}
            className="pointer-events-none absolute -top-8 -left-6"
          />
          <BotanicalSprig
            variant="twig"
            color="#F7F3ED"
            opacity={0.08}
            size={180}
            flip
            rotate={180}
            className="pointer-events-none absolute -bottom-4 -right-6"
          />
          <div className="text-center pb-6 border-b border-[#F7F3ED]/15 relative">
            {/* Sidebar logo — cropped from Kelly's Feb 23 mockup so the
                Planner header uses the exact "Willoah PLANNER" lockup with
                the botanical divider, not a live-text approximation.
                Wrapped in a Link so the brand mark behaves like every other
                site logo: tapping it takes you home. */}
            <NavLink
              to="/"
              data-testid="planner-sidebar-brand-link"
              aria-label="Back to Willoah home"
              className="block"
            >
              <img
                src="/willoah-planner-logo.png"
                alt="Willoah Planner"
                data-testid="planner-sidebar-logo"
                className="mx-auto w-40 h-auto select-none"
                draggable={false}
              />
            </NavLink>
          </div>
          <nav className="mt-8 flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
                    isActive
                      ? "bg-[#2f3a2a] text-[#F7F3ED]"
                      : "text-[#F7F3ED]/85 hover:bg-[#2f3a2a]/60 hover:text-[#F7F3ED]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.6} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto pt-8">
            <NavLink
              to="/grove"
              data-testid="planner-back-to-willoah"
              className="text-xs text-[#F7F3ED]/70 hover:text-[#F7F3ED] underline underline-offset-2"
            >
              ← Back to Willoah
            </NavLink>
          </div>
        </aside>

        {/* Main column */}
        <div className="flex-1 min-w-0">
          {/* Top bar (mobile shows brand; desktop shows a lighter header) */}
          <header
            data-testid="planner-topbar"
            className="sticky top-0 z-20 bg-[#F7F3ED]/95 backdrop-blur border-b border-[#A87F56]/20"
          >
            <div className="w-full px-4 sm:px-8 h-14 flex items-center justify-between">
              <div className="md:hidden shrink-0">
                {/* Mobile brand — reuses the same wordmark image as the
                    site-wide Navbar on Roots so the identity reads
                    identical across the site instead of drifting into a
                    live-text imitation. Wrapped in a Link so tapping the
                    wordmark always jumps back to Willoah home, matching
                    every other site logo. */}
                <NavLink
                  to="/"
                  data-testid="planner-mobile-brand-link"
                  aria-label="Back to Willoah home"
                  className="block"
                >
                  <img
                    src="/willow-wordmark.png"
                    alt="Willoah"
                    data-testid="planner-mobile-brand"
                    className="h-10 w-auto -my-1 select-none"
                    draggable={false}
                  />
                </NavLink>
              </div>
              <div className="hidden md:block text-sm text-[#2A2823]/70">
                {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  aria-label="Notifications"
                  data-testid="planner-notifications"
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[#2A2823]/70 hover:bg-[#A87F56]/10"
                >
                  <Bell className="w-4 h-4" />
                </button>
                <ProfileMenu />
              </div>
            </div>
          </header>

          <main className="w-full px-4 sm:px-8 py-6 sm:py-10 pb-32 md:pb-16">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav
        data-testid="planner-mobile-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#F7F3ED] border-t border-[#A87F56]/25"
      >
        <div className="grid grid-cols-5 h-16 items-center">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center text-[10px] gap-1 ${
                  isActive ? "text-[#3E4A37]" : "text-[#2A2823]/60"
                }`
              }
            >
              <Icon className="w-5 h-5" strokeWidth={1.6} />
              <span className="tracking-wide">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Mobile floating "+" — routes to /planner/calendar quick-add */}
      <NavLink
        to="/planner/calendar?add=1"
        data-testid="planner-mobile-add"
        aria-label="Quick add"
        className="md:hidden fixed bottom-11 left-1/2 -translate-x-1/2 z-40 w-14 h-14 rounded-full bg-[#A87F56] text-[#F7F3ED] flex items-center justify-center shadow-lg hover:bg-[#a37f45] transition-colors"
      >
        <Plus className="w-6 h-6" strokeWidth={2} />
      </NavLink>
    </div>
  );
}
